/**
 * The three facts the get-going card writes down, per business, on this phone.
 *
 * In `sync_meta` beside `active_tenant` and the pull cursors, and not in a
 * table of its own: it is one small JSON value keyed by business, exactly what
 * that table holds, and `forgetDevice()` already wipes it, so the next account
 * on this phone never inherits the last one's skips. Never sent to the server:
 * a skip is this person's answer on this phone, and the card on their other
 * phone asks again.
 *
 * The shape and the rules are `setup-steps.ts`; this file only reads and writes.
 */
import { eq } from 'drizzle-orm';

import { getDb } from '@/lib/db';
import { syncMeta } from '@/lib/db-schema';
import { parseProgress, type SetupProgress } from '@/lib/setup-steps';

const keyFor = (tenantId: string): string => `setup:${tenantId}`;

export const getSetupProgress = (tenantId: string): SetupProgress => {
  const row = getDb()
    .select({ value: syncMeta.value })
    .from(syncMeta)
    .where(eq(syncMeta.key, keyFor(tenantId)))
    .get();
  return parseProgress(row === undefined ? null : row.value);
};

/** The whole progress, in one write. Written on the tap, so a crash cannot undo a skip. */
export const saveSetupProgress = (tenantId: string, progress: SetupProgress): void => {
  const value = JSON.stringify(progress);
  getDb()
    .insert(syncMeta)
    .values({ key: keyFor(tenantId), value })
    .onConflictDoUpdate({ target: syncMeta.key, set: { value } })
    .run();
};
